<template>
  <article class="wwh-card" :class="{ 'is-reversed': reverse }">
    <!-- Media -->
    <div class="wwh-media">
      <img :src="image" alt="" class="wwh-img" />
    </div>

    <!-- Content -->
    <div class="wwh-content">
      <div class="wwh-top">
        <span class="badge" v-if="tags">{{ tags }}</span>
        <img v-if="logo" :src="logo" alt="Company logo" class="company-logo" />
      </div>

      <h3 class="title">{{ title }}</h3>

      <p class="desc">
        {{ description }}
      </p>

      <button class="cta" @click="goToStory">
        Read the full story
        <v-icon size="small">mdi-arrow-right</v-icon>
      </button>
    </div>
  </article>
</template>

<script setup>
import { defineProps } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps({
  image: { type: String, required: true },
  logo: { type: String, default: '' },
  tags: { type: String, default: '' },
  title: { type: String, required: true },
  description: { type: String, required: true },
  route: { type: String, required: true },
  /** Flip layout: image on right, content on left */
  reverse: { type: Boolean, default: false },
})

const router = useRouter()
const goToStory = () => {
  router.push(props.route).then(() =>
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
  )
}
</script>

<style scoped>
/* Card container uses CSS GRID internally (not for page layout). */
.wwh-card {
  display: grid;
  grid-template-columns: 520px 1fr; /* image | content like the comp */
  gap: 1rem;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e9eef5;
  box-shadow: 0 6px 18px rgba(0,0,0,0.06);
  padding: 16px;
  margin: 1rem;
  overflow: hidden;
}
.is-reversed { grid-template-columns: 1fr 520px; }
.is-reversed .wwh-media { order: 2; }
.is-reversed .wwh-content { order: 1; }

.wwh-media {
  background: #ffe6e0; /* matches the top card tint in the design */
  border-radius: 12px;
  overflow: hidden;
  min-height: 240px;
  display: block;
}
.wwh-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.wwh-content {
  border-radius: 12px;
  padding: 8px 8px 8px 8px;
  text-align: left;
}

.wwh-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  background: #f9fafb;
}
.company-logo { height: 28px; }

.title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 8px 0 8px 0;
}
.desc {
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 12px;
}

.cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #007ab2;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
}
.cta:hover { background: #00aeff; }

/* Responsive: stack image/content on mobile */
@media (max-width: 960px) {
  .wwh-card {
    grid-template-columns: 1fr;
    padding: 12px;
  }
  .wwh-media { min-height: 200px; background: #f3f4f6; }
}
</style>

